import {
  Button,
  FormControl,
  FormErrorMessage,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  Flex,
  ButtonGroup,
  InputRightElement,
  Tooltip,
  CloseButton,
  FormErrorIcon,
} from '@chakra-ui/react';
import {
  useMemo,
  useCallback,
  ReactNode,
  useState,
  useRef,
  ChangeEvent,
  useEffect,
} from 'react';
import { t } from '@transifex/native';
import { T } from '@transifex/react';
import styled from '@emotion/styled';
import { mdiMagnify, mdiPlus } from '@mdi/js';
import { ErrorBoundary } from 'react-error-boundary';
import { useQueryErrorResetBoundary } from 'react-query';
import { RouteComponentProps, useHistory } from 'react-router-dom';
import {
  DataTable,
  DataTableListOptions,
  DataTableSortingRule,
} from 'features/common/components/DataTable';
import { useDrawerStateDispatch } from 'features/main/context';
import { PortalIcon } from 'features/common/components/PortalIcon';
import { allowedSearchCharactersRegexModified } from 'features/common/validations/matchers';
import { useOrgGroups } from 'features/user-group/user-group-list/api/useOrgGroups';
import { openDrawer } from 'features/main/context/drawer/actionCreators';
import { MembersErrorPage } from 'features/main/components/CustomErrorPages/MembersErrorPage';
import {
  getTableOptions,
  transformDataForTable,
} from 'features/user-group/user-group-list/components/groupListTableHelpers';
import {
  adminPath,
  userGroupListPath,
} from 'features/admin/components/admin-paths';
import { useSearchOnList } from 'features/common/hooks/search/useSearchOnList';
import { useDebounce } from 'features/common/hooks/useDebounce';
import { useFilteredPaginatedData } from '../../hooks/useFilteredPagination';
import { UserGroupPlaceholder } from '../UserGroupPlaceholder';

const TableDiv = styled.div`
  table {
    display: block;
    overflow-x: auto;
    white-space: nowrap;
  }
`;

export type IUserGroupTableProps = RouteComponentProps<{ page: string }>;

const Header = ({ children }: { children: ReactNode }) => (
  <Flex
    flexDirection={['column', 'column', 'row']}
    justifyContent='space-between'
    marginBottom={12}
    gap={[6, 6, 0]}
  >
    {children}
  </Flex>
);

const usePaginationSync = (initialPage: string | undefined) => {
  const [page, setPage] = useState<number>(parseInt(initialPage ?? '1', 10));
  const [pageSize, setPageSize] = useState<number>(10);
  const setPageToFirstOnSearch = useCallback(() => setPage(1), []);
  return { page, pageSize, setPage, setPageSize, setPageToFirstOnSearch };
};

export const GroupListTableView = (props: IUserGroupTableProps) => {
  const { reset } = useQueryErrorResetBoundary();
  return (
    <ErrorBoundary FallbackComponent={MembersErrorPage} onReset={reset}>
      <GroupListTable {...props} />
    </ErrorBoundary>
  );
};

const sortBy: DataTableSortingRule[] | undefined = [];
const GROUP_LIST_TABLE_SEARCH_FIELDS: Array<'name'> = [
  'name',
];

export const GroupListTable = (props: IUserGroupTableProps) => {
  const history = useHistory();
  const [searchFilter, setSearchFilter] = useState<string>('');
  const debouncedSearchFilter = useDebounce(searchFilter);

  const { page, pageSize, setPage, setPageSize, setPageToFirstOnSearch } =
    usePaginationSync(props.match.params.page);

  const [, drawerDispatch] = useDrawerStateDispatch();
  const [searchStringIsValid, setSearchStringIsValid] = useState<boolean>(true);

  const {
    data: orgGroupData,
    isLoading: orgGroupLoading,
    isRefetching,
  } = useOrgGroups();
  const orgGroupDataTransformed =
    transformDataForTable(orgGroupData?.data) ?? [];

  const filteredData = useSearchOnList(
    orgGroupDataTransformed,
    GROUP_LIST_TABLE_SEARCH_FIELDS,
    debouncedSearchFilter,
  );

  const {
    paginated: orgGroupDataPaginated,
    filteredCount: filteredPagination,
  } = useFilteredPaginatedData(filteredData, page, pageSize);

  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    setPageToFirstOnSearch();
  }, [debouncedSearchFilter, setPageToFirstOnSearch]);

  // To handle pagination when the filtered data length is less than pageSize
  useEffect(() => {
    if (
      !orgGroupLoading &&
      orgGroupDataPaginated.length === 0 &&
      filteredPagination > 0
    ) {
      //Check if the current page is greater than the last page
      const lastPage = Math.ceil(filteredPagination / pageSize) || 1;
      if (page > lastPage) {
        setPage(lastPage);
        // Update the URL to reflect the last page
        history.push({
          pathname: `${adminPath}${userGroupListPath}/page/${lastPage}`,
          search: history.location.search,
        });
      }
    }
  }, [
    orgGroupDataPaginated.length,
    filteredPagination,
    page,
    pageSize,
    setPage,
    orgGroupLoading,
    history,
  ]);

  const options = useMemo(() => getTableOptions(), []);

  const onChange = useCallback(
    (options: DataTableListOptions) => {
      const searchParams = new URLSearchParams(history.location.search);
      searchParams.delete('tab');
      const search = searchParams.toString();

      history.push({
        pathname: `${adminPath}${userGroupListPath}/page/${options.page}`,
        search: search ? `?${search}` : '',
      });
      setPage(options.page);
      setPageSize((options as { pageSize?: number })?.pageSize ?? 10);
    },
    [history, setPage, setPageSize],
  );

  const onSearch = (event: ChangeEvent<HTMLInputElement>) => {
    const searchInputValue = event.target.value;

    if (allowedSearchCharactersRegexModified.test(searchInputValue)) {
      setSearchFilter(searchInputValue);
      setSearchStringIsValid(true);
    } else setSearchStringIsValid(false);
  };

  const onClearHandler = () => {
    setSearchFilter('');
    setSearchStringIsValid(true);
  };

  const onClickRow = (row: any) => {
    history.push({
      pathname: `${adminPath}${userGroupListPath}/${row.id}/members`,
      state: { page },
    });
  };

  const pagination = filteredPagination < pageSize ? 'hidden' : 'complex';

  return (
    <>
      <Flex direction='column'>
        <Header>
          <Heading as='h1'>
            <T _str='User groups' />
          </Heading>
          <ButtonGroup
            display='flex'
            flexDirection={['column', 'row']}
            gap={2}
            spacing={0}
          >
            <Button
              variant='solid'
              data-testid='create_team_toggle'
              onClick={() => drawerDispatch(openDrawer('create-team-drawer'))}
              leftIcon={<PortalIcon path={mdiPlus} />}
              data-behavior-analytics-id='create group drawer - Create group page'
              isDisabled={orgGroupDataTransformed.length >= 50}
            >
              <T _str='Create' />
            </Button>
          </ButtonGroup>
        </Header>
        <Flex justifyContent='space-between' marginBottom={12} maxW='xs'>
          <FormControl isInvalid={!searchStringIsValid}>
            <InputGroup mb='4' maxWidth='sm'>
              <InputLeftElement
                pointerEvents='none'
                children={<PortalIcon path={mdiMagnify} />}
              />
              <Input
                placeholder='Search'
                aria-label='Search user groups'
                data-testid='org_user_group_search'
                data-behavior-analytics-feature='group search'
                onChange={onSearch}
                value={searchFilter}
              />
              {searchFilter.length > 0 && (
                <InputRightElement>
                  <Tooltip label='Clear search'>
                    <CloseButton
                      onClick={onClearHandler}
                      data-testid='group_search_clear_button'
                    />
                  </Tooltip>
                </InputRightElement>
              )}
            </InputGroup>
            <FormErrorMessage>
              <FormErrorIcon />
              <T _str='Currently, the search is only available in alphanumeric characters, at signs (@), dots, dashes, and underscores.' />
            </FormErrorMessage>
          </FormControl>
        </Flex>

        {!orgGroupLoading && orgGroupDataPaginated.length === 0 ? (
          debouncedSearchFilter.length ? (
            <UserGroupPlaceholder
              description={t('Change your search parameters and try again.')}
              title={t('No groups found')}
              src='https://delivery-sitecore.sitecorecontenthub.cloud/api/public/content/spot-magnify-close-neutral'
            />
          ) : (
            <UserGroupPlaceholder
              description={t(
                'A group lets you assign app roles to all its users, making it easier to manage permissions for multiple users.',
              )}
              title={t("Create your organization's first group")}
              src='https://delivery-sitecore.sitecorecontenthub.cloud/api/public/content/spot-account-group-neutral'
            />
          )
        ) : (
          <TableDiv>
            <DataTable
              columns={options.columns}
              rowActions={options.rowActions}
              pagination={pagination}
              totalItems={filteredPagination}
              data={orgGroupDataPaginated}
              onChange={onChange}
              onClickRow={(row) => onClickRow(row)}
              page={page}
              pageSize={pageSize}
              isLoading={orgGroupLoading || isRefetching}
              sortBy={sortBy}
              theaderProps={{ 'data-behavior-analytics-id': 'group sorting' }}
              paginationProps={{
                'data-behavior-analytics-feature': ' group pagination',
              }}
              data-behavior-analytics-feature='group table'
            />
          </TableDiv>
        )}
      </Flex>
    </>
  );
};
